import { DragDropProvider, type DragEndEvent } from '@dnd-kit/react';
import type { DropCell, PlacementResult, TileData } from '@words-happen/game-components';
import {
  Board,
  cellFromDragEvent,
  fitCellSize,
  RackDropMonitor,
  TileDragOverlay,
  TileRack,
  tilePointerSensors,
} from '@words-happen/game-components';
import {
  ArrowRightIcon,
  CheckIcon,
  ChevronLeftIcon,
  PlusIcon,
  ShuffleIcon,
  SwapIcon,
  WordsHappenButton,
  WordsHappenFormAlert,
} from '@words-happen/ui-components';
import type React from 'react';
import { type RefObject, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { LobbyFrame } from '../games/lobby-frame';
import lobbyStyles from '../games/lobby-screen.module.scss';
import { ScreenFrame } from '../screen-frame';
import {
  boardGameDraftReducer,
  boardTilesOf,
  createDraft,
  landRackTile,
  placementsOf,
  tileIds,
} from './board-game-draft';
import { endLine, statusLine, tilesLabel, turnLine } from './board-game-labels';
import { previewMove } from './board-game-preview';
import styles from './board-game-screen.module.scss';
import type { BoardGameMoveOutcome, BoardGamePlacement, BoardGameView } from './board-game-types';

/** The rack holds this many characters: the rules' RACK_CAPACITY, in every rules version so far. */
const RACK_CAPACITY = 8;

const BOARD_CELL_SIZE = 32;
const MIN_BOARD_CELL_SIZE = 20;
const BOARD_GAP = 1;
const BOARD_PADDING = 6;
/** The board's own border, which its width doesn't count under content-box sizing. */
const BOARD_CHROME = 4;

const RACK_CELL_SIZE = 36;
const MIN_RACK_CELL_SIZE = 24;
const RACK_GAP = 4;
/** The tray's padding and slot gap, from `tile-rack.module.scss`. */
const RACK_TRAY_PADDING = 10;
const RACK_SLOT_GAP = 10;

const RACK_ID = 'rack';
const SWAP_TRAY_ID = 'swap-tray';
const BOARD_ID = 'board';

const isSlotOf = (rackId: string, targetId: unknown): boolean =>
  typeof targetId === 'string' && targetId.startsWith(`${rackId}-slot-`);

export interface BoardGameScreenProps {
  /** `loading` until the game first arrives; `not-found` for a game that isn't the player's. */
  status: 'loading' | 'ready' | 'not-found' | 'load-error';
  /** The game as the player sees it; set once `status` is `ready`. */
  game?: BoardGameView;
  /** The dictionary, for the score preview. Without it there is no preview, and the server still judges. */
  isWord?: (word: string) => boolean;
  /** Why the last move didn't go through, or that the game had moved on. */
  notice?: string;
  /** Lays the tiles; under swap mode then-play, swaps `swapped` first, in the same turn. */
  onPlay: (placed: BoardGamePlacement[], swapped: number[]) => Promise<BoardGameMoveOutcome>;
  /** A scoreless turn; under then-play, swaps `swapped` first, in the same turn. */
  onPass: (swapped: number[]) => Promise<BoardGameMoveOutcome>;
  /** Under swap mode ends-turn, the swap is the whole turn. */
  onSwap: (tiles: number[]) => Promise<BoardGameMoveOutcome>;
  onRetryLoad: () => void;
  /** Back to the games list. */
  onBack: () => void;
  onNewGame: () => void;
  className?: string;
}

/** The width the board and rack have to lay out in, measured, so the drop maths uses the cell size drawn. */
const useMeasuredWidth = (ref: RefObject<HTMLElement | null>): number | null => {
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);

  return width;
};

const boardCellSizeFor = (width: number | null, size: number): number =>
  width === null
    ? BOARD_CELL_SIZE
    : Math.max(
        MIN_BOARD_CELL_SIZE,
        Math.min(
          BOARD_CELL_SIZE,
          fitCellSize({ boardWidth: width - BOARD_CHROME, size, gap: BOARD_GAP, padding: BOARD_PADDING }),
        ),
      );

const rackCellSizeFor = (width: number | null): number =>
  width === null
    ? RACK_CELL_SIZE
    : Math.max(
        MIN_RACK_CELL_SIZE,
        Math.min(
          RACK_CELL_SIZE,
          Math.floor((width - 2 * RACK_TRAY_PADDING - (RACK_CAPACITY - 1) * RACK_SLOT_GAP) / RACK_CAPACITY),
        ),
      );

const Scoreboard: React.FC<{ game: BoardGameView }> = ({ game }) => (
  <ul className={styles.players} aria-label="Scores">
    {game.players.map((player) => {
      const toPlay = game.status === 'active' && player.isMe === game.isMyTurn;
      return (
        <li
          key={player.seat}
          className={[styles.player, toPlay && styles.toPlay].filter(Boolean).join(' ')}
          aria-current={toPlay ? 'true' : undefined}
        >
          <span className={styles.playerName}>{player.isMe ? 'You' : player.name}</span>
          <span className={styles.playerScore}>{player.score}</span>
          {toPlay ? <span className={styles.visuallyHidden}>to play</span> : null}
        </li>
      );
    })}
  </ul>
);

const TurnHistory: React.FC<{ game: BoardGameView }> = ({ game }) => (
  <section className={styles.history} aria-labelledby="board-game-history">
    <h2 id="board-game-history" className={styles.historyTitle}>
      Turn history
    </h2>
    {game.turns.length === 0 ? (
      <p className={styles.quiet}>No turns played yet.</p>
    ) : (
      <ol className={styles.historyList} reversed>
        {[...game.turns].reverse().map((turn) => (
          <li key={turn.number}>{turnLine(turn, game.players)}</li>
        ))}
      </ol>
    )}
  </section>
);

const Finished: React.FC<{ game: BoardGameView } & Pick<BoardGameScreenProps, 'onBack' | 'onNewGame'>> = ({
  game,
  onBack,
  onNewGame,
}) => (
  <div className={styles.finished}>
    <p className={styles.finishedTitle}>{statusLine(game)}</p>
    {endLine(game) ? <p className={styles.quiet}>{endLine(game)}</p> : null}
    <div className={styles.actionsRow}>
      <WordsHappenButton variant="primary" icon={<PlusIcon />} onClick={onNewGame}>
        New game
      </WordsHappenButton>
      <WordsHappenButton onClick={onBack}>Your games</WordsHappenButton>
    </div>
  </div>
);

type PlayProps = Omit<BoardGameScreenProps, 'status' | 'game' | 'onRetryLoad'> & { game: BoardGameView };

/** An active or finished game: the board, the player's rack and, on their turn, the moves. */
const BoardGamePlay: React.FC<PlayProps> = ({
  game,
  isWord,
  notice,
  onPlay,
  onPass,
  onSwap,
  onBack,
  onNewGame,
  className,
}) => {
  const [draft, dispatch] = useReducer(boardGameDraftReducer, undefined, () => createDraft(game.rack, game.turnNumber));
  const [sending, setSending] = useState(false);
  const [landing, setLanding] = useState<TileData | null>(null);

  useEffect(() => {
    dispatch({ type: 'sync', rack: game.rack, turnNumber: game.turnNumber });
  }, [game.rack, game.turnNumber]);

  const rootRef = useRef<HTMLDivElement>(null);
  const width = useMeasuredWidth(rootRef);
  const boardCellSize = boardCellSizeFor(width, game.size);
  const rackCellSize = rackCellSizeFor(width);

  const locked = useMemo(() => boardTilesOf(game), [game]);
  const boardTiles = useMemo(() => [...locked, ...draft.pending], [locked, draft.pending]);
  const preview = useMemo(
    () => (game.ruleChecks ? previewMove(game, locked, draft.pending, isWord) : null),
    [game, locked, draft.pending, isWord],
  );

  const myTurn = game.status === 'active' && game.isMyTurn;
  const interactive = myTurn && !sending;
  const swapping = draft.swapTray !== null;
  const swapTray = draft.swapTray ?? [];
  const swapEndsTurn = game.swapMode === 'ends-turn';
  const bagCount = game.bagCount ?? 0;
  const swapLimit = Math.min(game.swapLimit, bagCount);
  const canSwap = myTurn && swapLimit > 0 && draft.rack.length + draft.pending.length > 0;
  const boardOpen = interactive && !(swapping && swapEndsTurn);

  const send = useCallback(async (move: () => Promise<BoardGameMoveOutcome>) => {
    setSending(true);
    try {
      if ((await move()) === 'refused') dispatch({ type: 'recall' });
    } finally {
      setSending(false);
    }
  }, []);

  const play = () => void send(() => onPlay(placementsOf(draft.pending), tileIds(swapTray)));
  const pass = () => void send(() => onPass(tileIds(swapTray)));
  const swap = () => void send(() => onSwap(tileIds(swapTray)));

  const dropGeometry = useMemo(
    () => ({ cellSize: boardCellSize, gap: BOARD_GAP, padding: BOARD_PADDING, size: game.size }),
    [boardCellSize, game.size],
  );
  const land = useCallback(
    (tile: TileData, cell: DropCell) => (boardOpen ? landRackTile(game, boardTiles, tile, cell) : null),
    [boardOpen, game, boardTiles],
  );
  const markForSwap = useCallback((tile: TileData) => dispatch({ type: 'mark', tile, limit: swapLimit }), [swapLimit]);
  const unmarkForSwap = useCallback((tile: TileData) => dispatch({ type: 'unmark', tile }), []);
  const handleRackDragEnd = useCallback(
    (tile: TileData, event: DragEndEvent) => {
      const target = event.operation.target?.id;
      if (isSlotOf(SWAP_TRAY_ID, target)) return markForSwap(tile);
      if (target !== BOARD_ID) return;
      const cell = cellFromDragEvent(event, dropGeometry);
      const landed = cell ? land(tile, cell) : null;
      if (landed) dispatch({ type: 'lay', tile: landed });
    },
    [dropGeometry, land, markForSwap],
  );
  const handleTrayDragEnd = useCallback(
    (tile: TileData, event: DragEndEvent) => {
      if (isSlotOf(RACK_ID, event.operation.target?.id)) unmarkForSwap(tile);
    },
    [unmarkForSwap],
  );
  const handleTileMove = useCallback(
    (placement: PlacementResult) => dispatch({ type: 'move', tile: placement.tile }),
    [],
  );
  const handleTileTurn = useCallback((tile: TileData) => dispatch({ type: 'move', tile }), []);
  const handleTileReturn = useCallback((tile: TileData) => dispatch({ type: 'return', tile }), []);
  const handleReorder = useCallback((tiles: TileData[]) => dispatch({ type: 'reorder', tiles }), []);

  const swapCount = swapTray.length;
  /** Under then-play, the swap goes with the play or pass: "Swap 2, then play". */
  const afterSwap = (verb: 'Play' | 'Pass') => (swapCount > 0 ? `Swap ${swapCount}, then ${verb.toLowerCase()}` : verb);

  return (
    <ScreenFrame className={className}>
      <header className={styles.topBar}>
        <WordsHappenButton
          size="sm"
          className={styles.back}
          aria-label="Back to your games"
          icon={<ChevronLeftIcon size={22} />}
          onClick={onBack}
        />
        <p className={styles.status} role="status">
          {statusLine(game)}
        </p>
        <span className={styles.bag}>{game.bagCount === null ? '' : `Bag ${game.bagCount}`}</span>
      </header>

      <div ref={rootRef} className={styles.main}>
        <Scoreboard game={game} />

        <DragDropProvider sensors={tilePointerSensors}>
          <div className={[styles.boardFrame, !boardOpen && myTurn && styles.boardLocked].filter(Boolean).join(' ')}>
            <Board
              standalone={false}
              shape={game.shape}
              size={game.size}
              tiles={boardTiles}
              cellSize={boardCellSize}
              gap={BOARD_GAP}
              padding={BOARD_PADDING}
              draggable={boardOpen}
              diagonals={game.diagonals}
              bonuses={game.bonuses}
              start={game.start ?? undefined}
              dropId={BOARD_ID}
              incomingTile={landing}
              onTilePlace={handleTileMove}
              onTileReturn={handleTileReturn}
              onTileRotate={handleTileTurn}
            />
          </div>

          {game.status === 'active' ? (
            <div className={styles.rackArea}>
              {swapping ? (
                <>
                  <span className={styles.rackLabel}>
                    To swap: {swapCount} of {swapLimit}
                  </span>
                  <TileRack
                    id={SWAP_TRAY_ID}
                    standalone={false}
                    tiles={swapTray}
                    capacity={RACK_CAPACITY}
                    cellSize={rackCellSize}
                    gap={RACK_GAP}
                    draggable={interactive}
                    ariaLabel="Tiles to swap"
                    activateLabel="press to keep"
                    onTileActivate={unmarkForSwap}
                    onTileDragEnd={handleTrayDragEnd}
                  />
                </>
              ) : null}
              <TileRack
                id={RACK_ID}
                standalone={false}
                tiles={draft.rack}
                capacity={RACK_CAPACITY}
                cellSize={rackCellSize}
                gap={RACK_GAP}
                draggable={!sending}
                ariaLabel="Your rack"
                activateLabel="press to pick for swapping"
                onTileActivate={swapping && interactive ? markForSwap : undefined}
                onTileReorder={handleReorder}
                onTileDragEnd={handleRackDragEnd}
              />
            </div>
          ) : null}

          <RackDropMonitor
            region={RACK_ID}
            boardId={BOARD_ID}
            geometry={dropGeometry}
            land={land}
            onLandingChange={setLanding}
          />
          <TileDragOverlay />
        </DragDropProvider>

        {notice ? <WordsHappenFormAlert>{notice}</WordsHappenFormAlert> : null}

        {myTurn ? (
          <div className={styles.actions}>
            {preview && !notice ? (
              <p className={preview.valid ? styles.previewValid : styles.previewInvalid}>{preview.message}</p>
            ) : null}
            {swapping && swapEndsTurn ? (
              <>
                <WordsHappenButton
                  variant="primary"
                  icon={<SwapIcon />}
                  disabled={!interactive || swapCount === 0}
                  loading={sending}
                  onClick={swap}
                >
                  {swapCount === 0 ? 'Swap tiles' : `Swap ${tilesLabel(swapCount)}`}
                </WordsHappenButton>
                <p className={styles.quiet}>Tap tiles to pick them. Swapping uses your turn.</p>
                <WordsHappenButton size="sm" disabled={!interactive} onClick={() => dispatch({ type: 'cancel-swap' })}>
                  Cancel swap
                </WordsHappenButton>
              </>
            ) : (
              <>
                <WordsHappenButton
                  variant="primary"
                  icon={<CheckIcon />}
                  disabled={!interactive || draft.pending.length === 0}
                  loading={sending}
                  onClick={play}
                >
                  {afterSwap('Play')}
                </WordsHappenButton>
                <div className={styles.actionsRow}>
                  <WordsHappenButton
                    size="sm"
                    disabled={!interactive || draft.pending.length === 0}
                    onClick={() => dispatch({ type: 'recall' })}
                  >
                    Recall
                  </WordsHappenButton>
                  <WordsHappenButton
                    size="sm"
                    icon={<ShuffleIcon />}
                    disabled={sending}
                    onClick={() => dispatch({ type: 'shuffle' })}
                  >
                    Shuffle
                  </WordsHappenButton>
                  <WordsHappenButton
                    size="sm"
                    icon={<ArrowRightIcon />}
                    disabled={!interactive || draft.pending.length > 0}
                    onClick={pass}
                  >
                    {afterSwap('Pass')}
                  </WordsHappenButton>
                  {swapping ? (
                    <WordsHappenButton
                      size="sm"
                      disabled={!interactive}
                      onClick={() => dispatch({ type: 'cancel-swap' })}
                    >
                      Cancel swap
                    </WordsHappenButton>
                  ) : (
                    <WordsHappenButton
                      size="sm"
                      icon={<SwapIcon />}
                      disabled={!interactive || !canSwap}
                      title={canSwap ? undefined : 'No swaps: the bag is empty or this game allows none'}
                      onClick={() => dispatch({ type: 'open-swap' })}
                    >
                      Swap
                    </WordsHappenButton>
                  )}
                </div>
                {swapping ? (
                  <p className={styles.quiet}>
                    Tap tiles to pick them, then play or pass: the swap happens first, in the same turn.
                  </p>
                ) : null}
              </>
            )}
          </div>
        ) : null}

        {game.status === 'active' && !game.isMyTurn ? (
          <p className={styles.quiet}>Their move shows here as soon as they make it. You can rearrange your rack.</p>
        ) : null}

        {game.status === 'finished' ? <Finished game={game} onBack={onBack} onNewGame={onNewGame} /> : null}

        <TurnHistory game={game} />
      </div>
    </ScreenFrame>
  );
};

/** The game when it can't be played: still loading, not the player's, failed to load, waiting or cancelled. */
const GameMessage: React.FC<
  Pick<BoardGameScreenProps, 'onBack' | 'onNewGame' | 'className'> & {
    title: string;
    children?: React.ReactNode;
  }
> = ({ title, children, onBack, onNewGame, className }) => (
  <LobbyFrame title={title} backLabel="Back to your games" onBack={onBack} className={className}>
    {children}
    <div className={lobbyStyles.inlineActions}>
      <WordsHappenButton variant="primary" onClick={onBack}>
        Your games
      </WordsHappenButton>
      <WordsHappenButton icon={<PlusIcon />} onClick={onNewGame}>
        New game
      </WordsHappenButton>
    </div>
  </LobbyFrame>
);

/**
 * A two-player board game (/games/:id) as one player sees it: both scores, whose turn it is, the board, the player's
 * own rack, the bag count and the turn history. On the player's turn they lay tiles and play, pass or swap; the score
 * shown before they send is only a preview, and the server's answer is what's recorded. A refused move puts the tiles
 * back on the rack. It holds no network state: the app loads the game, sends the moves and keeps the game current.
 */
export const BoardGameScreen: React.FC<BoardGameScreenProps> = ({ status, game, onRetryLoad, className, ...props }) => {
  if (status === 'loading') {
    return (
      <LobbyFrame title="Game" backLabel="Back to your games" onBack={props.onBack} className={className}>
        <p className={lobbyStyles.status} role="status">
          Loading the game…
        </p>
      </LobbyFrame>
    );
  }
  if (status === 'load-error') {
    return (
      <LobbyFrame title="Game" backLabel="Back to your games" onBack={props.onBack} className={className}>
        <div className={lobbyStyles.error}>
          <WordsHappenFormAlert title="Couldn't load the game">
            Check your connection and try again.
          </WordsHappenFormAlert>
          <WordsHappenButton size="sm" onClick={onRetryLoad}>
            Try again
          </WordsHappenButton>
        </div>
      </LobbyFrame>
    );
  }
  if (status === 'not-found' || !game) {
    return (
      <GameMessage title="Game not found" {...props} className={className}>
        <p className={lobbyStyles.lead}>This game doesn't exist, or you're not playing in it.</p>
      </GameMessage>
    );
  }
  if (game.status === 'waiting' || game.status === 'cancelled') {
    return (
      <GameMessage title={statusLine(game)} {...props} className={className}>
        <p className={lobbyStyles.lead}>
          {game.status === 'waiting'
            ? 'The game starts as soon as your opponent joins. It shows in your games when it does.'
            : 'It was cancelled before anyone joined.'}
        </p>
      </GameMessage>
    );
  }

  return <BoardGamePlay game={game} className={className} {...props} />;
};
